import React, { ReactElement } from 'react';
import { ColorFrom, Feature } from '@tencent/workbench';
import BaseDrawerFeature from '@framework/workbench/common/feature/base-drawer-feature';
import {
    FillSelectKey,
    FillSelectTypes,
    FillTypeToModelMap,
    FillTypeToSelectKeyMap,
    LINEAR_GRADIENT,
    RADIAL_GRADIENT,
} from './config';
import { IFillFeature, IFillGradualValues, IFillImageValues } from './fill-interface';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import i18n from '@i18next/i18n';
import { alphaToOpacity, opacityToAlpha } from '@framework/workbench/drawer/utils';
import {
    FillType,
    GradientType,
    IBackgroundFillInfo,
    IGradFill,
    IGradFillPoint,
    IImageFill,
    IImageTile,
    ImageTileFlipType,
} from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { FONT_FILL_KEY } from '@src/slide/domain/ui-model/element-modeles/property/text/constants';
import {
    color2HexAndAlpha,
    getColorNoAlpha,
    getColorRGB,
} from '@src/slide/infrastructure/module/independent/color/transform';
import { AngleFrom, NumberInputFrom } from '@tencent/wb-ext-form';
import {
    DirectList,
    getConfigByRect,
    getImageAlign,
    getLinearDirection,
    getMirrorType,
    getRectDirection,
    ShapeDirectionList,
} from '@editor/src/js/constants';
import GradualPreset from '@framework/workbench/drawer/components/gradual-preset';
import _cloneDeep from 'lodash/cloneDeep';
import { FormItemName, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import { TdwDrawerConstMap, tdwDrawerReport } from '@framework/workbench/drawer/report';
import { MULTI } from '@src/js/constant';
import { EEffectAlgn } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import { CUSTOM } from '@src/slide/infrastructure/module/dependent/config/tob/constant';
import {
    DirectOptions,
    LinearDirectOptions,
    RECT_GRADIENT,
    RectDirectOptions,
    SHAPE_GRADIENT,
    ShapeDirectOptions,
} from '@framework/workbench/drawer/ui/fill/constants';
import { ColorSelectButton } from '@framework/workbench/drawer/components/color-select-button';
import {
    TDWVer5Type,
    TDWVer6Type,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { STSchemeColorVal } from '@tencent/slide-sdk-pb-model';
import { handleInputBlurInitAndReset } from '@src/slide/application/service/drawer-input-manager/drawer-handler';
import { copy } from '@src/slide/infrastructure/module/independent/object-operation';
import { ColorPickerType, ColorSourceType } from '@src/js/common/ark-extensions/dc-components/color-picker/type';
import ColorPickerContainer from '@src/js/common/ark-extensions/dc-components/color-picker/color-picker-container';
import ImageQuickLookManager
    from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-quick-look-manager';
import {
    GradualDirection,
    PictureFill,
    GradualSetting,
    OperationType,
    IDirectionOption,
    RadioItem,
    IGradualPoint as IDrawerGradualPoint,
} from '@tencent/drawing-drawer-components';
import { IGradualPoint } from '@framework/workbench/drawer/ui/fill/gradual-fill/gradual-fill-feature.interface';

// 纯色填充默认值
const DEFAULT_SOLID_COLOR = '#000000';
// 渐变填充默认值
const DEFAULT_GRADUAL_ANGLE = 135;
const DEFAULT_GRADUAL_START_COLOR = '#FFF2CC';
const DEFAULT_GRADUAL_END_COLOR = '#FFB700';
const DEFAULT_GRADUAL_ADD_COLOR = '#ffffff';
const DEFAULT_GRADUAL_POINT_ARR = [
    {
        id: GradualSetting.startPoint,
        left: 0,
        bgColor: new TDColor({ rgbaStr: DEFAULT_GRADUAL_START_COLOR }),
        percent: 0,
    },
    {
        id: GradualSetting.endPoint,
        left: GradualSetting.gradientContentWidth - GradualSetting.pointWidth,
        bgColor: new TDColor({ rgbaStr: DEFAULT_GRADUAL_END_COLOR }),
        percent: 100,
    },
];
// 图片填充默认值
const DEFAULT_IMAGE_IMG = CUSTOM.IMAGE.DEFAULT_FILL_IMAGE;
const DEFAULT_IMAGE_TILE: IImageTile = {
    tx: 0, ty: 0, sx: 100, sy: 100, flip: ImageTileFlipType.NONE, algn: EEffectAlgn.TL,
};
const DEFAULT_IMAGE_ALPHA = 1;
const FILL_BG_TYPE_ALL = i18n.t('充满');
const FILL_BG_TYPE_TILE = i18n.t('平铺');
const DEFAULT_WHITE_COLOR = '#000000';

@Feature()
export class FillFeature extends BaseDrawerFeature implements IFillFeature {
    /**
     * 获取设置新透明度后的色值
     * @param opacity
     * @param color
     */
    public static getOpacityChangedColor = (opacity: number, color: string): string => {
        const alpha = opacityToAlpha(opacity) ?? 1;
        const rgb = getColorRGB(color);
        return `rgb(${rgb[0]},${rgb[1]},${rgb[2]},${alpha})`;
    };

    /**
     * 将数据上的渐变点数据转换成渲染组件需要的格式
     * @param index
     * @param gradFill
     * @param item
     * @private
     */
    public static transformPoint(index: number, gradFill: IGradFill, item: IGradFillPoint): {
        id: string,
        left: number,
        bgColor: TDColor,
        percent: number,
    } {
        const maxOffset = GradualSetting.gradientContentWidth - GradualSetting.pointWidth;
        let id = '';
        let percent = 0;
        if (index === 0) {
            id = GradualSetting.startPoint;
            percent = 0;
        } else if (index === gradFill.gsList.length - 1) {
            id = GradualSetting.endPoint;
            percent = 100;
        } else {
            id = `${GradualSetting.pointIndex}-gradual-arrow`;
            GradualSetting.pointIndex += 1;
        }

        percent = item ? item.pos : percent;
        const bgColor = item?.color ? item.color : new TDColor({
            rgbaStr: DEFAULT_GRADUAL_ADD_COLOR,
        });

        const left = Math.round(percent / 100 * maxOffset);
        return {
            id, left, bgColor, percent,
        };
    }
    // 纯色填充 - 对应的临时编辑数据
    public fillSolidValues: ITDColor = new TDColor({ rgbaStr: DEFAULT_SOLID_COLOR });
    // 渐变填充 - 对应的临时编辑数据
    public fillGradualValues: IFillGradualValues = {};
    // 图片填充 - 对应的临时编辑数据
    public fillImageValues: IFillImageValues = {};
    // 图案填充 - 对应的临时编辑数据
    public fillPattValues = '';

    // 从model获取出来的数据
    private fillInfoInDrawer = {};
    // 是否包含多中文字填充样式
    private isMultiDifferent = false;
    private isChangeGradualDirection = false;
    // 图片上传是否采用剪贴板（数据上报用）
    private imageUploadWithClip = false;
    // 临时储存渐变信息，方便对比是否要更新sdk
    private sdkAlpha?: number;

    public constructor() {
        super();
        this.addInitPropsCallback(INPUT_PROPS_CALLBACK_KEY.TEXT_FILL);
    }

    /**
     * 更新当前页面的填充数据源
     * 并生成当前填充类型对应的临时编辑数据
     */
    public initPropsCallback = (): void => {
        this.fillInfoInDrawer = { ...this.getFillInfoFromModel() };
        this.initSolidValues();
        this.initGradualValues();
        this.initImageValues();
        this.initPattValues();
    };

    /**
     * 获取下拉列表的填充方式
     */
    public getFillTypes = (): { key: string, content: string }[] => {
        // 导入的文字如果是图案填充，则下拉选项中要包含此选项，只用作显示
        const needFilterPatt = !this.fillInfoInDrawer[FillType.PattFill];
        if (needFilterPatt) {
            return FillSelectTypes.filter(e => e.key !== FillSelectKey.PATT_FILL);
        }
        return FillSelectTypes;
    };

    /**
     * 获取当前选中的填充方式
     */
    public getCheckedFillType = (): string => {
        const checkedKey = Object.keys(this.fillInfoInDrawer)
            .find(key => !!this.fillInfoInDrawer[key]) || '';
        if (!this.isMultiDifferent && !FillTypeToSelectKeyMap[checkedKey]) {
            return FillSelectKey.NO_FILL;
        }
        return FillTypeToSelectKeyMap[checkedKey] || FillSelectKey.NO_SELECT;
    };

    /**
     * 填充主类型切换
     * @param type
     */
    public handleFillTypeChange = (type: string): void => {
        let ver5;
        const values = {};
        // 先将所有文本填充相关的属性置为空
        Object.values(FillTypeToModelMap).forEach((p) => {
            values[p] = undefined;
        });
        // 再将选中的填充项设置上
        if (type === FillSelectKey.NO_FILL) {
            values[FillTypeToModelMap[FillType.NoFill]] = 1;
            ver5 = 1;
        } else if (type === FillSelectKey.SOLID_FILL) {
            this.fillSolidValues = new TDColor({ themeKey: STSchemeColorVal.tx1, rgbaStr: DEFAULT_SOLID_COLOR });
            values[FillTypeToModelMap[FillType.SolidFill]] = this.fillSolidValues;
            // todo: 接完主题后就可以把这块删掉了
            // @ts-ignore
            values.color = this.fillSolidValues.srgb_color;
            // @ts-ignore
            values.tdColor = this.fillSolidValues;
            ver5 = 2;
        } else if (type === FillSelectKey.GRADUAL_FILL) {
            this.fillGradualValues = {
                activePointId: DEFAULT_GRADUAL_POINT_ARR[0].id,
                activeColor: DEFAULT_GRADUAL_POINT_ARR[0].bgColor,
                curGradualType: LINEAR_GRADIENT,
                curDirect: DirectList[0],
                curAngle: DEFAULT_GRADUAL_ANGLE,
                pointArray: _cloneDeep(DEFAULT_GRADUAL_POINT_ARR),
                presetItem: GradualPreset.getPresetItemByStyle('gra-preset-3'),
            };
            values[FillTypeToModelMap[FillType.GradualFill]] = this.getGradualValues();
            ver5 = 3;
        } else if (type === FillSelectKey.IMAGE_FILL) {
            if (!this.fillImageValues.imgData) {
                // 已有历史编辑数据，则保留，与客户端交互保持一致
                this.fillImageValues = {
                    curImgFillType: FILL_BG_TYPE_ALL,
                    imgData: { src: DEFAULT_IMAGE_IMG },
                    alpha: DEFAULT_IMAGE_ALPHA,
                };
            }
            values[FillTypeToModelMap[FillType.ImageFill]] = this.getImageValues();
            ver5 = 4;
        }
        this.submitAction(ActionType.FONT_FILL, values);
        // 上报数据
        this.tdwDrawerReport(FormItemName.FONT_FILL_SELECT, { ver5 });
    };

    /**
     * 纯色填充 - 颜色变更
     * @param tdColor
     * @param from
     * @param hasAlpha
     */
    public handleSolidColorChange = (tdColor: ITDColor, from: ColorFrom | number, hasAlpha: boolean): void => {
        this.fillSolidValues = tdColor;
        const values = {
            [FillTypeToModelMap[FillType.SolidFill]]: this.fillSolidValues.srgb_color,
        };
        // todo: 主题过渡
        // @ts-ignore
        values.tdColor = this.fillSolidValues;
        this.submitAction(ActionType.FONT_FILL, values);
        this.getColorPickerManager()?.colorPickerTdwReport({
            color: tdColor.srgb_color,
            colorPickerFrom: TDWVer6Type.COLOR_PICKER_DRAWER,
            colorGroupFrom: from,
            actionType: TDWVer5Type.COLOR_PICKER_FILL,
        });
    };

    /**
     * 纯色填充 - 透明度变更
     * @param opacity
     * @param from
     * @param onlyChangeUI
     */
    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.TEXT_FILL)
    public handleSolidOpacityChange(opacity: number, from?: NumberInputFrom, dragEnd?: boolean): void {
        this.fillSolidValues.updateOpacity(opacity);
        const isOnlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
        if (isOnlyChangeUI) {
            return;
        }
        const values = {
            [FillTypeToModelMap[FillType.SolidFill]]: this.fillSolidValues.srgb_color,
        };
        // todo: 主题过渡
        // @ts-ignore
        values.tdColor = this.fillSolidValues;
        this.submitAction(ActionType.FONT_FILL, values);
        // 上报数据
        this.tdwDrawerReportNumberInput(FormItemName.FONT_SOLID_FILL_OPACITY, { ver6: opacity }, from);
    }

    /**
     * 渐变填充 - 切换类型
     * @param type
     */
    public handleGradualTypeChange = (type: string): void => {
        if (this.fillGradualValues.curGradualType !== type) {
            this.fillGradualValues.curGradualType = type;
            this.fillGradualValues.curDirect = this.getFillDirectionOptions()[0];
        }
        this.fillGradualValues.presetItem = null;
        const values = {
            [FillTypeToModelMap[FillType.GradualFill]]: this.getGradualValues(),
        };
        this.submitAction(ActionType.FONT_FILL, values);
        // 上报数据
        const ver5 = type === LINEAR_GRADIENT ? 1 : 2;
        this.tdwDrawerReport(TdwDrawerConstMap.fontGradualFillLinerOrRadial, { ver5 });
    };

    /**
     * 渐变填充 - 切换线性角度
     * @param value
     * @param from
     * @param isDragging
     */
    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.TEXT_FILL)
    public handleGradualAngleChange(value: number, from?: AngleFrom, dragEnd?: boolean): void {
        this.fillGradualValues.curAngle = value;
        this.fillGradualValues.presetItem = !dragEnd
            ? null : GradualPreset.compareWithPreset(this.fillInfoInDrawer[FillType.GradualFill], value);
        const isOnlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
        if (isOnlyChangeUI) {
            return;
        }
        const values = {
            [FillTypeToModelMap[FillType.GradualFill]]: this.getGradualValues(),
        };
        this.submitAction(ActionType.FONT_FILL, values);
        // 上报数据
        this.tdwDrawerReportAngle(FormItemName.FONT_GRADUAL_FILL_ANGLE, from, value);
    }

    /**
     * 渐变填充 - 切换径向方向
     * @param value
     */
    public handleGradualDirectionChange = (value: { id: string, rect: any, title: string }): void => {
        this.fillGradualValues.curDirect = value;
        this.isChangeGradualDirection = true;
        const values = {
            [FillTypeToModelMap[FillType.GradualFill]]: this.getGradualValues(),
        };
        this.submitAction(ActionType.FONT_FILL, values);
        // 上报数据
        this.tdwDrawerReport(FormItemName.FONT_GRADUAL_FILL_DIRECTION, { ver5: value?.title });
        this.isChangeGradualDirection = false;
    };

    /**
     * 渐变填充 - 透明度变更
     * @param opacity
     * @param from
     * @param onlyChangeUI
     */
    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.TEXT_FILL)
    public handleGradualOpacityChange(opacity: number, from?: NumberInputFrom, dragEnd?: boolean): void {
        const newColor = this.fillGradualValues.activeColor || DEFAULT_GRADUAL_POINT_ARR[0].bgColor;
        newColor.updateOpacity(opacity);
        this.fillGradualValues.activeColor = newColor;
        this.fillGradualValues.presetItem = null;
        const curPoint = this.fillGradualValues.pointArray.find(e => e.id === this.fillGradualValues.activePointId);
        if (curPoint) {
            curPoint.bgColor = newColor;
            const isOnlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
            if (isOnlyChangeUI) {
                return;
            }
            const values = {
                [FillTypeToModelMap[FillType.GradualFill]]: this.getGradualValues(),
            };
            this.submitAction(ActionType.FONT_FILL, values);
            // 上报数据
            this.tdwDrawerReportNumberInput(FormItemName.FONT_GRADUAL_FILL_OPACITY, { ver6: opacity }, from);
        }
    }

    /**
     * 渐变填充 - 渲染编辑组件
     */
    public renderGradualFill = (): ReactElement => <GradualSetting
        pointArray={this.getPointArray()}
        activePointId={this.fillGradualValues.activePointId}
        updatePoint={this.handleGradualUpdatePoint}
        getDropContent={() => <ColorPickerContainer
            colorPickerType={ColorPickerType.StandardPC}
            onColorChange={this.handlePaletteGradualColorChange}
            selectedTdColor={this.fillGradualValues.activeColor}
        />}
    />;

    public renderGradualDirection = () => {
        const { curGradualType } = this.fillGradualValues;
        let path = GradientType.Linear;
        if (curGradualType === RADIAL_GRADIENT) {
            path = GradientType.Radial;
        } else if (curGradualType === RECT_GRADIENT) {
            path = GradientType.Rect;
        } else if (curGradualType === SHAPE_GRADIENT) {
            path = GradientType.Shape;
        }
        return <GradualDirection
            path={path}
            directionOptions={this.getFillDirectionOptions()}
            tooltip={i18n.t('方向')}
            pointArray={this.getPointArray()}
            handleChange={this.handleGradualDirectionChange}
            getCurrentDirection={() => this.fillGradualValues.curDirect as IDirectionOption}
        ></GradualDirection>;
    };

    /**
     * 图片填充 - 从文件夹读取图片
     */
    public handleImageBgFromFile = async () => {
        const copyPasteImageService = await this.getAppServices().editableServiceLayer
            ?.copyPasteImageService?.promise();
        const { ImageEditManager } = copyPasteImageService ?? {};
        const imageManager = ImageEditManager?.getInstance();
        const input = document.getElementById('select_pic_input');
        input?.click();
        if (imageManager) {
            imageManager.imageReplaceAction = 3; // 代表是替换背景图片
            imageManager.imageReplacing = true;
            imageManager.clipDataWaiting = false;
        }
        this.imageUploadWithClip = false;
    };

    /**
     * 图片填充 - 从剪贴板读取图片
     */
    public handleImageBgFromClipBoard = async () => {
        const copyPasteImageService = await this.getAppServices().editableServiceLayer
            ?.copyPasteImageService?.promise();
        const { ImageEditManager } = copyPasteImageService ?? {};
        const imageManager = ImageEditManager?.getInstance();
        if (imageManager) {
            // 从剪贴板中获取
            imageManager.imageReplaceAction = 3; // 代表是替换背景图片
            imageManager.clipDataWaiting = true;
        }
        this.imageUploadWithClip = true;
    };

    /**
     * 图片填充 - 渲染编辑组件
     */
    public renderImgFill(): ReactElement {
        return <PictureFill
            curImgFillType={this.fillImageValues.curImgFillType || FILL_BG_TYPE_ALL}
            onFillBgTypeChange={this.handleFillBgTypeChange}
            curImageAlign={this.fillImageValues.curImageAlign || ''}
            onImageAlignChange={this.handleImageAlignChange}
            curMirrorImageType={this.fillImageValues.curMirrorImageType || ''}
            onMirrorImageTypeChange={this.handleMirrorImageTypeChange}
            imageSrc={this.getImageSrc()}
        />;
    }

    /**
     * 图片填充 - 切换透明度
     * @param value
     * @param onlyChangeUI
     * @param from
     */
    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.TEXT_FILL)
    public handleImageOpacityChange(value: number, from?: NumberInputFrom, dragEnd?: boolean): void {
        this.fillImageValues.alpha = opacityToAlpha(value);
        const isOnlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
        if (isOnlyChangeUI) {
            return;
        }
        if (this.fillImageValues.alpha === this.sdkAlpha) return;
        const values = {
            [FillTypeToModelMap[FillType.ImageFill]]: this.getImageValues(),
        };
        this.submitAction(ActionType.FONT_FILL, values);
        // 上报数据
        this.tdwDrawerReportNumberInput(FormItemName.FONT_IMAGE_FILL_OPACITY, { ver6: value }, from);
    }

    /**
     * 获取颜色透明度
     * @param color
     */
    public getColorOpacity = (): number => {
        // todo: 直接取alpha
        const color = this.fillSolidValues;
        if (!color) {
            return 0;
        }
        const { alpha } = color;
        return alphaToOpacity(alpha);
    };

    public getFillDirectionOptions() {
        if (this.fillGradualValues.curGradualType === LINEAR_GRADIENT) {
            return LinearDirectOptions;
        }
        if (this.fillGradualValues.curGradualType === RECT_GRADIENT) {
            return RectDirectOptions;
        }
        if (this.fillGradualValues.curGradualType === SHAPE_GRADIENT) {
            return ShapeDirectOptions;
        }
        return DirectOptions;
    }

    public shouldAngleDisabled(): boolean {
        const fillType = this.getCheckedFillType();
        return !(fillType ===  FillSelectKey.GRADUAL_FILL
            && this.fillGradualValues.curGradualType === LINEAR_GRADIENT);
    }

    public getColorSelectButton() {
        return <ColorSelectButton
            onColorChange={(tdColor, from) => {
                const parseColor = tdColor?.srgb_color || DEFAULT_WHITE_COLOR;
                this.handleSolidColorChange(tdColor, from, parseColor.startsWith('rgba'));
            }}
            // 选中态接入主题后改为传对象
            value={this.fillSolidValues || new TDColor({
                rgbaStr: DEFAULT_WHITE_COLOR,
            })}
            paletteButtonsStyle={{
                width: 116,
                marginLeft: 'auto',
            }}
            colorBoxStyle={{
                width: 88,
            }}
        />;
    }

    public renderFillContent = () => {
        this.initPropsCallback();
        const options = this.getFillTypes();
        const value = this.getCheckedFillType();
        return <RadioItem radioOptions={options} onChange={this.handleFillTypeChange} value={value} />;
    };

    private getPointArray = (): IDrawerGradualPoint[] => this.fillGradualValues.pointArray;

    /**
     * 体积数据
     * @param actionType
     * @param values
     * @private
     */
    private submitAction(actionType: string, values: { [key: string]: any }) {
        this.updateProps(actionType, values);
        // 因为数据在ui-sdk会进行一次转换再保存到sdk，但是editor-model中的paragraphList并没有同步，这会导致页面新编辑的和sdk生成出来的数据格式不统一
        // 从而在判断多选元素是同一设置时会判断出错，所以下面在设置完成后会刷新一次editor
        this.callbackByActionType(ActionType.FONT_REFRESH);
    }

    /**
     * 从model获取最新的文本填充数据
     * @private
     */
    private getFillInfoFromModel(): IBackgroundFillInfo {
        let fontFill = this.getItemValue(FONT_FILL_KEY);
        if (fontFill === MULTI) {
            this.isMultiDifferent = true;
            fontFill = undefined;
        } else {
            this.isMultiDifferent = false;
        }
        return fontFill || {};
    }

    /**
     * 图片填充 - 切换填充方式
     * @param value
     */
    private handleFillBgTypeChange = (value: string): void => {
        this.fillImageValues.curImgFillType = value;
        const values = {
            [FillTypeToModelMap[FillType.ImageFill]]: this.getImageValues(),
        };
        this.submitAction(ActionType.FONT_FILL, values);
        const key = FormItemName.FONT_IMAGE_FILL_STYLE;
        const ver5 = value === FILL_BG_TYPE_ALL ? 1 : 2;
        tdwDrawerReport(key, { ver5 });
    };

    /**
     * 图片填充 - 切换对齐方式
     * @param value
     */
    private handleImageAlignChange = (value: string): void => {
        this.fillImageValues.curImageAlign = value as EEffectAlgn;
        const values = {
            [FillTypeToModelMap[FillType.ImageFill]]: this.getImageValues(),
        };
        this.submitAction(ActionType.FONT_FILL, values);
    };

    /**
     * 图片填充 - 切换镜像类型
     * @param value
     */
    private handleMirrorImageTypeChange = (value: ImageTileFlipType): void => {
        this.fillImageValues.curMirrorImageType = value;
        const values = {
            [FillTypeToModelMap[FillType.ImageFill]]: this.getImageValues(),
        };
        this.submitAction(ActionType.FONT_FILL, values);
    };

    /**
     * 渐变填充 - 渐变点切换
     * @param id
     * @param pointArray
     * @param onlyChangeUI
     * @param operation
     */
    private handleGradualUpdatePoint = (
        id: string,
        pointArray: IDrawerGradualPoint[],
        onlyChangeUI: boolean,
        operation?: string,
    ): void => {
        pointArray = pointArray.map((item) => {
            const bgColor = item.bgColor instanceof TDColor ? item.bgColor
                : new TDColor({ rgbaStr: item.bgColor.srgbColor });
            return {
                ...item,
                bgColor,
            };
        }) as IGradualPoint[];
        // 切换渐变点时，强刷一下，否则透明度不更新
        if (this.fillGradualValues.activePointId !== id) {
            this.formService.$forceUpdate();
        }
        this.fillGradualValues.activePointId = id || this.fillGradualValues.activePointId;
        this.fillGradualValues.activeColor = (pointArray as IGradualPoint[])
            .find(e => e.id === this.fillGradualValues.activePointId)?.bgColor || this.fillGradualValues.activeColor;
        this.fillGradualValues.pointArray = pointArray || this.fillGradualValues.pointArray;
        this.fillGradualValues.presetItem = null;
        if (onlyChangeUI) {
            return;
        }
        const values = {
            [FillTypeToModelMap[FillType.GradualFill]]: this.getGradualValues(),
        };
        this.submitAction(ActionType.FONT_FILL, values);
        if (operation) {
            const formItemKey = {
                [OperationType.ADD]: TdwDrawerConstMap.fontGradualAddPoint,
                [OperationType.REMOVE]: TdwDrawerConstMap.fontGradualRemovePoint,
                [OperationType.REVERT]: TdwDrawerConstMap.fontGradualRevertPoint,
            }[operation];
            tdwDrawerReport(formItemKey);
        }
    };

    /**
     * 渐变填充 - 色板切换
     * @param color
     * @param from
     */
    // eslint-disable-next-line max-len
    private handlePaletteGradualColorChange = (color: ITDColor, from: ColorSourceType): void => {
        const curPoint = this.fillGradualValues.pointArray.find(e => e.id === this.fillGradualValues.activePointId);
        if (curPoint) {
            this.fillGradualValues.activeColor = color;
            curPoint.bgColor = color;
            const values = {
                [FillTypeToModelMap[FillType.GradualFill]]: this.getGradualValues(),
            };
            this.submitAction(ActionType.FONT_FILL, values);
            // 上报数据
            let ver5 = 1;
            if (from === ColorSourceType.Custom) {
                ver5 = 3;
            } else if (from === ColorSourceType.Recent) {
                ver5 = 2;
            }
            this.tdwDrawerReport(TdwDrawerConstMap.fontGradualPointColorChange, { ver5 });
        }
    };

    /**
     * 获取设置新透明度后的色值
     * @param opacity
     * @param color
     */
    private getOpacityChangedColor = (opacity: number, color: string): string => {
        const alpha = opacityToAlpha(opacity) ?? 1;
        const rgb = getColorRGB(color);
        return `rgb(${rgb[0]},${rgb[1]},${rgb[2]},${alpha})`;
    };

    /**
     * 将数据上的渐变点数据转换成渲染组件需要的格式
     * @param index
     * @param gradFill
     * @param item
     * @private
     */
    private transformPoint(index: number, gradFill: IGradFill, item: IGradFillPoint): {
        id: string,
        left: number,
        bgColor: string,
        percent: number,
    } {
        const maxOffset = GradualSetting.gradientContentWidth - GradualSetting.pointWidth;
        let id = '';
        let percent = 0;
        if (index === 0) {
            id = GradualSetting.startPoint;
            percent = 0;
        } else if (index === gradFill.gsList.length - 1) {
            id = GradualSetting.endPoint;
            percent = 100;
        } else {
            id = `${GradualSetting.pointIndex}-gradual-arrow`;
            GradualSetting.pointIndex += 1;
        }

        percent = item ? item.pos : percent;
        const bgColor = item?.color ? item.color.srgb_color : DEFAULT_GRADUAL_ADD_COLOR;

        const left = Math.round(percent / 100 * maxOffset);
        return {
            id, left, bgColor, percent,
        };
    }

    /**
     * 从临时编辑数据整合最新的渐变填充数据
     */
    private getGradualValues = (): IGradFill => {
        const values: any = {};
        values.gsList = this.fillGradualValues.pointArray.map((item) => {
            let color;
            if (typeof item.bgColor === 'string') {
                color = { srgb_color: item.bgColor };
            } else {
                color = item.bgColor;
            }
            return  { pos: item.percent, color, id: '' };
        });
        if (this.fillGradualValues.curGradualType === LINEAR_GRADIENT) {
            // 线性渐变
            const angle = this.isChangeGradualDirection
                ? this.fillGradualValues.curDirect?.angle : this.fillGradualValues.curAngle;
            values.lin = { ang: angle ?? DEFAULT_GRADUAL_ANGLE, scaled: 1 };
            values.pathGradient = { path: GradientType.Linear };
        } else {
            let path = GradientType.Radial;
            if (this.fillGradualValues.curGradualType === RECT_GRADIENT) {
                path = GradientType.Rect;
            } else if (this.fillGradualValues.curGradualType === SHAPE_GRADIENT) {
                path = GradientType.Shape;
            }
            // 径向渐变
            const curDirect = this.fillGradualValues.curDirect || DirectList[0];
            const fillToRect = Object.assign({}, curDirect.rect);
            values.pathGradient = { path, fillToRect };
            values.tileRect = Object.assign({}, curDirect.tileRect);
        }
        return values;
    };

    /**
     * 从临时编辑数据整合最新的图片填充数据
     */
    private getImageValues = (): IImageFill => {
        const values: IImageFill = {
            src: this.fillImageValues.imgData?.src ?? DEFAULT_IMAGE_IMG,
            alpha: this.fillImageValues.alpha ?? DEFAULT_IMAGE_ALPHA,
        };
        if (this.fillImageValues.curImgFillType === FILL_BG_TYPE_TILE) {
            values.tile = _cloneDeep(DEFAULT_IMAGE_TILE);
            if (this.fillImageValues.curMirrorImageType) {
                values.tile.flip = this.fillImageValues.curMirrorImageType;
            }
            if (this.fillImageValues.curImageAlign) {
                values.tile.algn = this.fillImageValues.curImageAlign;
            }
        }
        return values;
    };

    private getImageSrc = (): string => ImageQuickLookManager.getInstance()
        .getDisplayUrl(this.fillImageValues.imgData?.src || '');

    /**
     * 预设切换
     * @param value
     */
    private handleFillPresetChange = (value): void => {
        const values = {};
        // 先将所有文本填充相关的属性置为空
        Object.values(FillTypeToModelMap).forEach((p) => {
            values[p] = undefined;
        });
        let ver5;
        if (value) {
            if (value.curFillType === FillSelectKey.SOLID_FILL) {
                this.fillSolidValues = value.solidFillColor;
                values[FillTypeToModelMap[FillType.SolidFill]] = this.fillSolidValues;
                // todo: 接完主题后就可以把这块删掉了
                // @ts-ignore
                values.tdColor = this.fillSolidValues;
                // @ts-ignore
                values.color = this.fillSolidValues.srgb_color;
                ver5 = 2;
            }
            if (value.curFillType === FillSelectKey.GRADUAL_FILL) {
                this.fillGradualValues = {
                    activePointId: value.activePointId,
                    curGradualType: value.curGradualType,
                    curAngle: value.gradualAngle,
                    pointArray: value.pointArray,
                    presetItem: value.presetItem,
                };
                values[FillTypeToModelMap[FillType.GradualFill]] = this.getGradualValues();
                ver5 = 3;
            }
        } else {
            values[FillTypeToModelMap[FillType.NoFill]] = 1;
            ver5 = 1;
        }
        this.submitAction(ActionType.FONT_FILL, values);
        // 上报数据
        this.tdwDrawerReport(FormItemName.FONT_FILL_PRESET, { ver5 });
    };

    /**
     * 初始化编辑纯色填充时的临时数据
     */
    private initSolidValues = (): void => {
        const solidData = this.getItemValue(ActionType.FONT_THEME_COLOR);
        if (!solidData) {
            this.fillSolidValues = new TDColor({
                themeKey: STSchemeColorVal.tx1,
                rgbaStr: DEFAULT_SOLID_COLOR,
            });
            return;
        }
        this.fillSolidValues = solidData;
    };

    /**
     * 初始化编辑渐变填充时的临时数据
     */
    private initGradualValues = (): void => {
        const gradualData = this.fillInfoInDrawer[FillType.GradualFill];
        if (!gradualData) {
            this.fillGradualValues = {};
            return;
        }
        const { curAngle, curGradualType, curDirect } = this.processGradualData(gradualData);
        // 渐变点
        let pointArray = _cloneDeep(DEFAULT_GRADUAL_POINT_ARR);
        let { activePointId, activeColor } = this.fillGradualValues;
        const gsList = gradualData?.gsList;
        if (gsList.length) {
            // 新的pointArray
            pointArray = gsList.map((item, index) => FillFeature.transformPoint(index, gradualData, item));
            // 找到当前选中的渐进点index
            let curIndex = (this.fillGradualValues.pointArray || []).findIndex(e => e.id === activePointId);
            curIndex = curIndex === - 1 ? 0 : curIndex;
            // 转成成新的id
            activePointId = pointArray[curIndex].id;
            activeColor = pointArray.find(e => e.id === activePointId)?.bgColor || this.fillGradualValues.activeColor;
        }
        // 预设
        const presetItem = GradualPreset.compareWithPreset(gradualData);
        this.fillGradualValues = {
            ...this.fillGradualValues,
            pointArray,
            activePointId,
            activeColor,
            curAngle,
            curGradualType,
            curDirect,
            presetItem,
        };
    };

    private processGradualData(gradualData) {
        // 渐变类型、角度（线性）、方向（径向）
        let { curAngle, curGradualType, curDirect } = this.fillGradualValues;
        if (gradualData?.pathGradient) {
            const pathGrad = gradualData.pathGradient;
            if (pathGrad.path === 'linear') {
                curGradualType = LINEAR_GRADIENT;
                if (gradualData?.lin) {
                    curAngle = gradualData.lin.ang;
                }
                curDirect = getLinearDirection(curAngle ?? 0) as any;
            } else if (pathGrad.path === GradientType.Rect) {
                curGradualType = RECT_GRADIENT;
                curDirect = getRectDirection(pathGrad.fillToRect) as any;
            } else if (pathGrad.path === GradientType.Shape) {
                curGradualType = SHAPE_GRADIENT;
                curDirect = ShapeDirectionList[0] as any;
            } else {
                curGradualType = RADIAL_GRADIENT;
                curDirect = getConfigByRect(pathGrad.fillToRect);
                curAngle = undefined;
            }
        }
        return { curAngle, curGradualType, curDirect };
    }

    /**
     * 初始化编辑图片填充时的临时数据
     */
    private initImageValues = () => {
        const imageData = this.fillInfoInDrawer[FillType.ImageFill];
        if (!imageData) {
            return;
        }
        // 上报数据
        if (this.fillImageValues.imgData && (imageData.src !== this.fillImageValues.imgData.src)) {
            const key = this.imageUploadWithClip
                ? FormItemName.FONT_IMAGE_FILL_CLIPBOARD : FormItemName.FONT_IMAGE_FILL_UPLOAD;
            this.tdwDrawerReport(key);
        }
        this.fillImageValues.imgData = imageData;
        this.fillImageValues.alpha = imageData.alpha ?? DEFAULT_IMAGE_ALPHA;
        this.sdkAlpha = this.fillImageValues.alpha;
        if (imageData.tile) {
            // 平铺
            this.fillImageValues.curImgFillType = FILL_BG_TYPE_TILE;
            this.fillImageValues.curMirrorImageType = getMirrorType(imageData.tile.flip).id as ImageTileFlipType;
            this.fillImageValues.curImageAlign = getImageAlign(imageData.tile.algn).id;
        } else {
            // 充满
            this.fillImageValues.curImgFillType = FILL_BG_TYPE_ALL;
        }
    };

    /**
     * 初始化编辑图案填充时的临时数据
     */
    private initPattValues = (): void => {
        const pattData = this.fillInfoInDrawer[FillType.PattFill];
        if (!pattData) {
            this.fillPattValues = '';
            return;
        }
        this.fillPattValues = pattData;
    };
}
